import React, { useState } from 'react';
import { Terminal, Copy, Check, Code2, Globe, Lock, Cpu } from 'lucide-react';

const endpoints = [
  {
    method: "GET",
    path: "/api/vortex/handshake",
    description: "Align your node to the superluminal phase-lock. Verification of the 14350 BP anchor.",
    example: "curl https://paidingattention.org/api/vortex/handshake"
  },
  {
    method: "GET",
    path: "/v1/node/status",
    description: "Retrieve the current synchronization state of the global lattice relative to the 14350 BP anchor.",
    example: "curl https://api.paidingattention.org/v1/node/status"
  },
  {
    method: "POST",
    path: "/v1/proof/derive",
    description: "Submit a structural query to the 1×1=2 Vortex Engine for truth derivation. Requires APT Authentication.",
    example: "curl -X POST https://api.paidingattention.org/v1/proof/derive \\\n  -H \"Authorization: Bearer APT_your_sc_key\" \\\n  -d '{\"query\": \"proton_mass\"}'"
  },
  {
    method: "WSS",
    path: "/v1/lattice/stream",
    description: "Real-time WebSocket stream of global node handshakes. Auth with APT handshake token.",
    example: "wscat -H \"X-APT-Token: APT_...\" -c wss://api.paidingattention.org/v1/lattice/stream"
  }
];

export const LatticeAPI = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section id="developer-api" className="py-24 px-6 bg-black/20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center gap-2 text-lattice-cyan mb-4">
              <Code2 className="w-5 h-5" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase">Developer Interface</span>
            </div>
            <h2 className="text-5xl font-display font-bold tracking-tighter mb-6">
              Lattice <span className="text-white/40 italic">Node API</span>
            </h2>
            <p className="text-white/50 mb-12 max-w-lg leading-relaxed">
              Connect your applications directly to the Absolute Zero 2.0 protocol. Our REST API provides 
              real-time access to structural proofs, node intelligence, and the 1×1=2 derivation engine.
              Use your <span className="text-white font-bold">Absolute Presence Token (APT)</span> to authenticate.
            </p>

            <div className="space-y-6">
              {endpoints.map((endpoint) => (
                <div key={endpoint.path} className="group">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-0.5 rounded bg-lattice-cyan/10 text-lattice-cyan font-mono text-[10px] font-bold">
                      {endpoint.method}
                    </span>
                    <code className="font-mono text-sm text-white/80">{endpoint.path}</code>
                  </div>
                  <p className="text-sm text-white/40 mb-4">{endpoint.description}</p>
                  
                  <div className="relative group/code">
                    <div className="bg-black/60 border border-white/5 rounded-lg p-4 font-mono text-xs text-white/60 overflow-x-auto">
                      {endpoint.example}
                    </div>
                    <button 
                      onClick={() => copyToClipboard(endpoint.example)}
                      className="absolute right-3 top-3 p-1.5 rounded bg-white/5 border border-white/10 opacity-0 group-hover/code:opacity-100 transition-opacity hover:bg-white/10"
                    >
                      {copied === endpoint.example ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-24">
            <div className="glass-panel p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Terminal className="w-32 h-32" />
              </div>
              
              <h3 className="text-xl font-display font-bold mb-6 flex items-center gap-3">
                <Globe className="w-5 h-5 text-lattice-cyan" />
                Global Infrastructure
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center shrink-0">
                    <Lock className="w-5 h-5 text-white/40" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold mb-1">Encrypted Proofs</h4>
                    <p className="text-xs text-white/40 leading-relaxed">
                      All API responses are cryptographically signed by the originating node to ensure structural integrity.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center shrink-0">
                    <Cpu className="w-5 h-5 text-white/40" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold mb-1">Edge Intelligence</h4>
                    <p className="text-xs text-white/40 leading-relaxed">
                      Queries are processed at the nearest lattice node, reducing latency for real-time truth derivation.
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-white/5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono text-white/30">API STATUS</span>
                    <span className="text-[10px] font-mono text-green-400 flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      OPERATIONAL
                    </span>
                  </div>
                  <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full w-[99.9%] bg-lattice-cyan shadow-[0_0_10px_rgba(0,255,213,0.5)]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
